import { encode } from '@mapbox/polyline';
import { ToolOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Col,
  Collapse,
  Divider,
  Empty,
  Form,
  Image,
  message,
  Modal,
  Popconfirm,
  Popover,
  Row,
  Spin,
  Steps,
  Table,
  Tag,
} from 'antd';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';

// Components
import DeliveryForm from '../components/Forms/OrdersDetail/DeliveryForm';
import OrderInfoForm from '../components/Forms/OrdersDetail/OrderInfoForm';
import PickupForm from '../components/Forms/OrdersDetail/PickupForm';
import GoogleMap from '../components/Map/GoogleMap';
import Icon from '../components/Markers/icons/pickup';
import BreadCrumb from '../modules/breadcrumbs';
import PartnerLogo from '../components/PartnerLogo';
import { tenantConfig } from '../config/tenantConfig';

// Deps
import {
  DATE_BACKEND_FORMAT,
  DATE_EDITABLE_STATUSES,
  DAY_FORMAT,
  DAYTIME_FORMAT,
  ISSUE_TYPES,
  ORDER_STATUSES_DISPLAY,
  ORDER_TYPES,
  ORDER_TYPES_DISPLAY,
  TIME_FORMAT,
} from '../constants';
import { doGetOrderTickets } from '../modules/order-tickets/stores/thunk';
import { doDeleteOrders, doGetOrder, doUpdateOrder } from '../modules/orders/stores/thunk';
import { restoreOrder } from '../modules/orders/api';
import { doGetPartners } from '../modules/partners/stores/thunk';
import { selectAllPartners } from '../modules/partners/stores/slice';
import * as partnerApi from '../modules/partners/api';
import * as authApi from '../modules/auth/api';
import { getProgressStatus, imageMaker } from '../utils/common';
import request from '../utils/request';
import { computeCapturedWeight } from '../utils/orderWeight';
import useColumnConfig from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import OperatorActionPanel from '../components/OperatorActionPanel';
import OrderMediaSection from '../components/OrderMediaSection';
import OrderProductsEditor from '../components/OrderProductsEditor';
import OrderActualTimeEditor from '../components/OrderActualTimeEditor';
import OrderTicketsPanel from '../components/OrderTicketsPanel';
import OrderTimeline from '../components/OrderTimeline';
import './OrderDetail.scss';

const typePage = {
  order: {
    type: 'orderInfo',
    title: 'Edit Order Info',
  },
  pickup: {
    type: 'pickupInfo',
    title: 'Edit Pickup',
  },
  delivery: {
    type: 'deliveryInfo',
    title: 'Edit Delivery',
  },
};

const OrderDetailPage = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const order = useSelector((state) => state.order.currentOrder);
  const orderTickets = useSelector((state) => state.orderTickets.orderTickets);

  const isFetchingOrder = useSelector((state) => state.order.isFetching);
  const status = useSelector((state) => state.order.status);
  const action = useSelector((state) => state.order.action);
  const user = useSelector((state) => state.auth.user);
  const partners = useSelector(selectAllPartners);

  const isFetching = isFetchingOrder;
  const [messageApi, contextHolder] = message.useMessage();
  const role = user?.role?.toLowerCase();
  const isPartnerViewer = role === 'partner_viewer';
  // SERVICE = one visit at one address: the left panel renders as "Service"
  // (service time window/duration, no pickup/delivery framing) and the
  // Delivery panel is hidden entirely (Diego, Aug 18 2026).
  const isServiceOrder = order?.orderType === ORDER_TYPES.SERVICE;

  // Admin "view as driver" dry-run: mint a short-lived driver session for this
  // order's route, then open the real driver PWA (new tab) at this order's stop.
  // Everything the operator does there is a preview — nothing is saved.
  const [driverViewLoading, setDriverViewLoading] = useState(false);
  const handleDriverView = async () => {
    if (!order?.routeId) return;
    setDriverViewLoading(true);
    const { response, error } = await authApi.impersonateDriver(order.routeId);
    setDriverViewLoading(false);
    if (error || !response?.access_token) {
      messageApi.error(error?.message || 'Could not open the driver view');
      return;
    }
    // OVO-209 — REACT_APP_DRIVER_APP_URL bakes at build time. Only fall back to
    // localhost when we're actually running locally; on a deployed build a
    // missing/unset var must NOT silently open localhost:3003 (a dead tab for
    // operators) — surface an error instead.
    const isLocalHost = /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname);
    const configured = process.env.REACT_APP_DRIVER_APP_URL || (isLocalHost ? 'https://localhost:3003' : '');
    if (!configured) {
      messageApi.error('Driver app URL is not configured for this environment');
      return;
    }
    const base = configured.replace(/\/$/, '');
    const url = `${base}/dry-run#token=${encodeURIComponent(response.access_token)}&order=${encodeURIComponent(
      order.orderCode,
    )}`;
    window.open(url, '_blank', 'noopener');
  };

  // OVO-101 — the resolved Site badge is now clickable for admins: it opens the
  // partner's Sites catalog focused on that site (PartnerSites reads
  // ?focusSiteId and opens its editor). Partner viewers keep a plain tag —
  // they have no admin Sites access.
  const renderSiteBadge = (site) => {
    if (!site) return null;
    const tag = (
      <Tag color="blue" style={{ marginLeft: 8, fontSize: 11 }}>
        Site: {site.name}
      </Tag>
    );
    const partnerId = order?.partnerRef?.id ?? order?.partnerId;
    if (isPartnerViewer || !partnerId) return tag;
    return (
      <Link to={`/admin/partners/${partnerId}/sites?focusSiteId=${site.id}`} title={`Open site "${site.name}"`}>
        {tag}
      </Link>
    );
  };

  const breadCrumbs = isPartnerViewer
    ? [{ text: 'Orders', url: '/partner/orders' }, { text: order?.orderCode }]
    : [{ text: 'Orders', url: '/orders' }, { text: order?.orderCode }];

  const [titleModal, setTitleModal] = useState('');
  const [actionPage, setActionPage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [polylines, setPolylines] = useState([]);
  const [types, setTypes] = useState(order?.types || []);

  // Webhook logs
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [webhookLogsOpen, setWebhookLogsOpen] = useState(false);
  const [webhookLogsLoaded, setWebhookLogsLoaded] = useState(false);
  const [retryingLogId, setRetryingLogId] = useState(null);

  const onClickCopy = (value) => {
    copy(value);
    messageApi.open({
      type: 'success',
      style: {
        marginTop: '68px',
      },
      content: 'Copied',
    });
  };

  // Configurable field definitions for Order Info section
  const allFields = useMemo(() => {
    const fields = [
      {
        key: 'orderRef',
        title: 'Order Ref',
        render: (o) => (
          <div className="order-content hasCopy" onClick={() => onClickCopy(o.orderRef)}>
            {o.orderRef}
          </div>
        ),
      },
      {
        key: 'orderType',
        title: 'Order Type',
        render: (o) => (
          <div className="order-content">
            <span className={`status order ${o.orderType === ORDER_TYPES.SERVICE ? 'service' : ''}`}>
              {ORDER_TYPES_DISPLAY[o.orderType] || 'Standard'}
            </span>
          </div>
        ),
      },
      {
        key: 'deliveryDate',
        title: 'Delivery Date',
        render: (o) => <div className="order-content">{moment(o.deliveryDate).format(DAY_FORMAT)}</div>,
      },
      {
        key: 'volume',
        title: 'Volume',
        render: (o) => <div className="order-content">{o.volume}</div>,
      },
      {
        key: 'types',
        title: 'Types',
        render: (o) => <div className="order-content">{o.types ? o.types.join(', ') : ''}</div>,
      },
      {
        key: 'name',
        title: 'Name',
        render: (o) => <div className="order-content">{o.name}</div>,
      },
      {
        key: 'description',
        title: 'Description',
        // One line per product ("{name} -> {qty} Unit", the Airtable Item_description).
        render: (o) => (
          <div className="order-content" style={{ whiteSpace: 'pre-line' }}>
            {o.description || '-'}
          </div>
        ),
      },
      {
        key: 'size',
        title: 'Size (LxWxH)',
        render: (o) => {
          const parts = [o.sizeWidth, o.sizeLength, o.sizeHeight].filter(Boolean);
          return <div className="order-content">{parts.length ? parts.join(' x ') : '-'}</div>;
        },
      },
      {
        key: 'weight',
        // OVO-027 — renamed to "Original Weight" to disambiguate from the
        // captured-at-pickup value rendered below. Order.weight stays as the
        // dispatcher-declared / Airtable-imported value (authoritative for
        // billing on the dispatcher side); the second line surfaces the
        // driver-captured (or operator-overridden) effective weight, which
        // can differ when the driver weighed the package on site.
        title: 'Original Weight',
        render: (o) => {
          const captured = computeCapturedWeight(o);
          const showCaptured = captured && o.weight !== captured.value;
          return (
            <div className="order-content">
              <div>{o.weight ?? '-'}</div>
              {showCaptured && (
                <div
                  className="order-content-captured"
                  style={{ marginTop: 4, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6 }}
                >
                  <Tag color="blue" style={{ marginRight: 0 }}>
                    Captured
                  </Tag>
                  <strong>{captured.value}</strong>
                  <span>kg</span>
                  {captured.driverOnly && captured.driverName && (
                    <span style={{ color: '#9e9e9e', fontSize: 12 }}>· by {captured.driverName}</span>
                  )}
                </div>
              )}
            </div>
          );
        },
      },
      {
        key: 'partner',
        title: 'Partner',
        render: (o) => (
          <div className="order-content">
            {o.partnerRef?.id ? (
              <a href={`/admin/partners/${o.partnerRef.id}`} style={{ color: 'var(--accent-strong)' }}>
                {o.partnerRef.name || o.partner || '-'}
              </a>
            ) : (
              o.partner || '-'
            )}
          </div>
        ),
      },
      ...(!isPartnerViewer
        ? [
            {
              key: 'partnerContact',
              title: 'Partner Contact',
              render: (o) => <div className="order-content">{o.partnerContact || '-'}</div>,
            },
          ]
        : []),
      {
        key: 'partnerOrderRef',
        title: 'Partner Order Ref',
        render: (o) => <div className="order-content">{o.partnerOrderRef || '-'}</div>,
      },
      {
        key: 'tourRef',
        title: 'Tour Ref',
        render: (o) => <div className="order-content">{o.tourRef || '-'}</div>,
      },
      {
        key: 'airtableRef',
        title: 'Airtable Order Record Ref',
        render: (o) => <div className="order-content">{o.airtableOrderRecordRef || '-'}</div>,
      },
      {
        key: 'preparationMode',
        title: 'Preparation Mode',
        render: (o) => <div className="order-content">{o.preparationMode || '-'}</div>,
      },
      {
        key: 'collectionForm',
        title: 'Collection Form',
        render: (o) => (
          <div className="order-content">
            {o.collectionForm ? (
              <a href={o.collectionForm} target="_blank" rel="noopener noreferrer">
                Click to view
              </a>
            ) : (
              '-'
            )}
          </div>
        ),
      },
      {
        key: 'issueType',
        title: 'Issue Type',
        render: (o) => (
          <div className="order-content">{o.issueType ? ISSUE_TYPES[o.issueType] || o.issueType : '-'}</div>
        ),
      },
      {
        key: 'routeId',
        title: 'Route',
        // The order carries `routeId` (number FK), not the legacy `routePlanId`
        // / `routePlan.slug` shape this code was wired against — that branch
        // never fired and the field rendered "-" even when a route was set.
        // For partner viewers we route through /partner/routes/:id so the
        // partner-scoped detail page applies; admins land on /admin/routes/:id.
        render: (o) => {
          if (!o.routeId) return <div className="order-content">-</div>;
          const routeUrl = isPartnerViewer ? `/partner/routes/${o.routeId}` : `/admin/routes/${o.routeId}`;
          return (
            <div className="order-content">
              <a href={routeUrl} style={{ color: 'var(--accent-strong)' }}>
                #{o.routeId}
              </a>
            </div>
          );
        },
      },
    ];
    return fields;
  }, [isPartnerViewer]);

  const fieldConfig = useColumnConfig('order-detail-fields', allFields, []);

  useEffect(() => {
    setTypes(order?.types || []);
  }, [order?.types]);

  const fetchWebhookLogs = async () => {
    if (!order?.orderCode) return;
    const { response } = await partnerApi.getOrderWebhookLogs(order.orderCode);
    if (response) {
      setWebhookLogs(response);
    }
    setWebhookLogsLoaded(true);
  };

  const toggleWebhookLogs = () => {
    const nextOpen = !webhookLogsOpen;
    setWebhookLogsOpen(nextOpen);
    if (nextOpen && !webhookLogsLoaded) {
      fetchWebhookLogs();
    }
  };

  const handleRetryWebhook = async (logId) => {
    if (!order?.orderCode) return;
    setRetryingLogId(logId);
    try {
      const { error } = await partnerApi.retryWebhookLog(order.orderCode, logId);
      if (error) {
        messageApi.error('Retry failed');
      } else {
        messageApi.success('Webhook retried');
        await fetchWebhookLogs();
      }
    } catch {
      messageApi.error('Retry failed');
    } finally {
      setRetryingLogId(null);
    }
  };

  const logs = order?.logs || [];
  const statusDatetime = (status) => logs.find((l) => l.status === status)?.createdAt;

  const getPolylineAndMarkers = async () => {
    let newPolyline = [];
    const newMarkers = [
      {
        id: `${order.orderCode}-1`,
        content: <img src={imageMaker(Icon())} />,
        latitude: Number(order.pickupAddressLat),
        longitude: Number(order.pickupAddressLng),
        offsetX: 0,
        offsetY: 0,
        className: 'order-marker',
      },
      {
        id: `${order.orderCode}-2`,
        content: <img src="/icons/dropoff.svg" />,
        latitude: Number(order.deliveryAddressLat),
        longitude: Number(order.deliveryAddressLng),
        offsetX: 17,
        offsetY: -10,
        className: 'order-marker',
      },
    ];
    const code = encode([
      [order.pickupAddressLat, order.pickupAddressLng],
      [order.deliveryAddressLat, order.deliveryAddressLng],
    ]);
    const { response } = await request(`/common/polyline/${code}`, {
      method: 'GET',
    });
    if (response?.polyline) {
      newPolyline = [
        {
          id: code,
          polyline: response.polyline,
        },
      ];
    }
    setMarkers(newMarkers);
    setPolylines(newPolyline);
  };

  useEffect(() => {
    if (id) {
      setMarkers([]);
      setPolylines([]);
      dispatch(doGetOrder({ id }));
      if (!isPartnerViewer) {
        dispatch(doGetOrderTickets());
        dispatch(doGetPartners({ take: 1000000 }));
      }
    }
  }, [id]);

  useEffect(() => {
    if (
      order?.orderCode === id &&
      order?.pickupAddressLat &&
      order?.deliveryAddressLat &&
      order?.orderType !== ORDER_TYPES.SERVICE
    ) {
      getPolylineAndMarkers();
    }
  }, [order]);

  // OVO-028 — the old effect here redirected to the orders list when a
  // fetched order came back empty. That's replaced by an explicit
  // "order not found" page rendered below (see the `!order` guard), so a
  // deleted order shows a clear message instead of silently bouncing.

  useEffect(() => {
    if (action === 'update' && status === 'succeeded') {
      dispatch(doGetOrder({ id }));
      setIsModalOpen(false);
      setActionPage('');
    }
  }, [action, status]);

  const onClickEditInfo = () => {
    const ticketNames = order.tickets
      ?.map((ticketId) => orderTickets?.find((t) => t.id === ticketId)?.name)
      .filter((name) => !!name);
    form.setFieldsValue({
      orderType: order.orderType,
      orderRef: order.orderRef,
      status: order.status,
      deliveryDate: dayjs(order.deliveryDate),
      volume: order.volume,
      createdBy: order.createdBy,
      name: order.name,
      description: order.description,
      sizeLength: order.sizeLength,
      sizeWidth: order.sizeWidth,
      sizeHeight: order.sizeHeight,
      weight: order.weight,
      partnerId: order.partnerId || null,
      productDetailsKnown: order.productDetailsKnown !== false,
      orderProducts: (order.orderProducts || []).map((op) => ({
        partnerItemTypeId: op.partnerItemTypeId,
        quantity: op.quantity,
        weight: op.weight ?? null,
        notes: op.notes || '',
      })),
      partnerContact: order.partnerContact,
      partnerOrderRef: order.partnerOrderRef,
      tourRef: order.tourRef,
      airtableOrderRecordRef: order.airtableOrderRecordRef,
      preparationMode: order.preparationMode,
      collectionForm: order.collectionForm,
      ticketNames: ticketNames,
      issueType: order.issueType,
    });
    setActionPage(typePage.order.type);
    setTitleModal(typePage.order.title);
    setIsModalOpen(true);
  };

  const onClickEditPickup = () => {
    form.setFieldsValue({
      // Site + box so the picker opens on the order's current catalog link.
      pickupSiteId: order.pickupSite?.id ?? order.pickupSiteId ?? undefined,
      collectionPointId: order.collectionPoint?.id ?? order.collectionPointId ?? undefined,
      pickupName: order.pickupName,
      pickupEmail: order.pickupEmail,
      pickupAddress: {
        address: order.pickupAddress,
        latLng: {
          lat: order.pickupAddressLat,
          lng: order.pickupAddressLng,
        },
      },
      pickupPhone: order.pickupPhone,
      pickupDuration: order.pickupDuration,
      pickupNote: order.pickupNote,
      pickupTimeStart: order.pickupTimeStart ? dayjs(order.pickupTimeStart, TIME_FORMAT) : null,
      pickupTimeEnd: order.pickupTimeEnd ? dayjs(order.pickupTimeEnd, TIME_FORMAT) : null,
    });
    setActionPage(typePage.pickup.type);
    setTitleModal(typePage.pickup.title);
    setIsModalOpen(true);
  };

  const onClickEditDelivery = () => {
    const deliveryTimeStart = order.deliveryTimeStart ? dayjs(order.deliveryTimeStart, TIME_FORMAT) : null;
    const deliveryTimeEnd = order.deliveryTimeEnd ? dayjs(order.deliveryTimeEnd, TIME_FORMAT) : null;
    form.setFieldsValue({
      deliverySiteId: order.deliverySite?.id ?? order.deliverySiteId ?? undefined,
      deliveryName: order.deliveryName,
      deliveryEmail: order.deliveryEmail,
      deliveryPhone: order.deliveryPhone,
      deliveryDuration: order.deliveryDuration,
      deliveryNote: order.deliveryNote,
      deliveryAddress: {
        address: order.deliveryAddress,
        latLng: {
          lat: order.deliveryAddressLat,
          lng: order.deliveryAddressLng,
        },
      },
      deliveryTimeStart,
      deliveryTimeEnd,
    });
    setActionPage(typePage.delivery.type);
    setTitleModal(typePage.delivery.title);
    setIsModalOpen(true);
  };

  const cancelModal = () => {
    setIsModalOpen(false);
    setActionPage(undefined);
  };

  const onFinishForm = (values) => {
    let params = {};
    if (actionPage === typePage.order.type) {
      params = {
        sizeWidth: values.sizeWidth ? Number(values.sizeWidth) : null,
        sizeLength: values.sizeLength ? Number(values.sizeLength) : null,
        sizeHeight: values.sizeHeight ? Number(values.sizeHeight) : null,
        weight: values.weight ? Number(values.weight) : null,
        orderRef: values.orderRef,
        // deliveryDate is only editable pre-route (DRAFT / GEOCODING /
        // ERROR / UNASSIGNED). Only send it when the order is in one of
        // those statuses — the backend re-checks and silently ignores
        // it otherwise, but not sending it keeps the payload honest.
        ...(values.deliveryDate && DATE_EDITABLE_STATUSES.includes(order?.status)
          ? { deliveryDate: values.deliveryDate.format(DATE_BACKEND_FORMAT) }
          : {}),
        volume: values.volume ? Number(values.volume) : undefined,
        name: values.name,
        status: values.status,
        description: values.description,
        supportNote: values.supportNote,
        types,
        issueType: values.issueType,
        partnerId: values.partnerId || null,
        partner: values.partnerId ? partners.find((p) => p.id === values.partnerId)?.name || null : null,
        productDetailsKnown: values.productDetailsKnown !== false,
        orderProducts: values.orderProducts || [],
        partnerContact: values.partnerContact || null,
        partnerOrderRef: values.partnerOrderRef || null,
        tourRef: values.tourRef || null,
        airtableOrderRecordRef: values.airtableOrderRecordRef || null,
        preparationMode: values.preparationMode || null,
        collectionForm: values.collectionForm || null,
      };
    }
    if (actionPage === typePage.pickup.type) {
      params = {
        // OVO-101/253 — the catalog links, editable from the pickup form.
        pickupSiteId: values.pickupSiteId ?? null,
        collectionPointId: values.collectionPointId ?? null,
        pickupName: values.pickupName,
        pickupAddress: values?.pickupAddress?.address,
        pickupAddressLat: Number(values?.pickupAddress?.latLng?.lat),
        pickupAddressLng: Number(values?.pickupAddress?.latLng?.lng),
        pickupPhone: values.pickupPhone,
        pickupEmail: values.pickupEmail || null,
        pickupNote: values.pickupNote,
        pickupDuration: values.pickupDuration ? Number(values.pickupDuration) : null,
        pickupTimeStart: values.pickupTimeStart ? values.pickupTimeStart.format(TIME_FORMAT) : null,
        pickupTimeEnd: values.pickupTimeEnd ? values.pickupTimeEnd.format(TIME_FORMAT) : null,
      };
    }
    if (actionPage === typePage.delivery.type) {
      params = {
        deliverySiteId: values.deliverySiteId ?? null,
        deliveryName: values.deliveryName,
        deliveryAddress: values?.deliveryAddress?.address,
        deliveryAddressLat: Number(values?.deliveryAddress?.latLng?.lat),
        deliveryAddressLng: Number(values?.deliveryAddress?.latLng?.lng),
        deliveryPhone: values.deliveryPhone,
        deliveryEmail: values.deliveryEmail || null,
        deliveryNote: values.deliveryNote,
        deliveryDuration: values.deliveryDuration ? Number(values.deliveryDuration) : null,
        deliveryTimeStart: values.deliveryTimeStart ? values.deliveryTimeStart.format(TIME_FORMAT) : null,
        deliveryTimeEnd: values.deliveryTimeEnd ? values.deliveryTimeEnd.format(TIME_FORMAT) : null,
      };
    }
    dispatch(doUpdateOrder({ id, ...params }))
      .unwrap()
      .then(() => {
        dispatch(doGetOrder({ id }));
        dispatch(doGetOrderTickets());
      })
      .catch((error) => {
        messageApi.open({
          type: 'error',
          content: error.message,
        });
      });
  };

  const goRoutePlanDetail = (order, type) => {
    navigate('/routes/' + order.routePlan?.slug + '?orderCode=' + order.id + '&type=' + type);
  };

  const onValuesChange = (changedValues, allValues) => {
    console.log({ changedValues, allValues });
  };

  // POP/POD are PROOFS — dispatcher hint photos (OVO-103 + the Airtable
  // PICKUP_HINT ingest) live in their own "Photos for the driver" section
  // and must never leak into the proof panels.
  const HINT_FILE_TYPES = ['PICKUP_HINT', 'DELIVERY_HINT', 'SERVICE_HINT'];
  const proofFileOrders = order?.fileOrders?.filter((file) => !HINT_FILE_TYPES.includes(file.type)) || [];
  const filePickups = proofFileOrders.filter((file) => file.type !== 'PICKUP_DONE');
  const filePickupIds = filePickups.map((file) => file.id);
  const fileDeliveries = proofFileOrders.filter((file) => !filePickupIds.includes(file.id));

  // OVO-116 — soft-delete / restore from the order detail page. Deletable only
  // pre-route (same set as the date-editable statuses); a deleted order shows a
  // trash banner with Restore instead.
  const isDeletable = DATE_EDITABLE_STATUSES.includes(order?.status);

  const handleDeleteOrder = async () => {
    try {
      await dispatch(doDeleteOrders({ orderCodes: [order.orderCode] })).unwrap();
      messageApi.success(`Order ${order.orderCode} moved to trash — find it under the Trash tab on Orders.`);
      navigate('/admin/orders');
    } catch (e) {
      messageApi.error(e?.message || 'Failed to delete order');
    }
  };

  const handleRestoreOrder = async () => {
    try {
      await restoreOrder(order.orderCode);
      messageApi.success(`Order ${order.orderCode} restored`);
      dispatch(doGetOrder({ id }));
    } catch (e) {
      messageApi.error(e?.message || 'Failed to restore order');
    }
  };

  const content = (
    <div className="actions">
      {order?.trackingToken && (
        <Button
          type="link"
          onClick={() => onClickCopy(window.location.origin + '/t/' + order.orderCode + '/' + order.trackingToken)}
        >
          Share Tracking URL
        </Button>
      )}
    </div>
  );

  if (!order) {
    // OVO-028 — once the getOne fetch has resolved (succeeded or failed)
    // with no order, the order doesn't exist (e.g. it was deleted). Show
    // an explicit "not found" page instead of spinning forever / rendering
    // a stale shell. While the fetch is still in flight, keep spinning.
    const fetchResolved = action === 'getOne' && ['succeeded', 'failed'].includes(status);
    if (fetchResolved) {
      return (
        <div className="OrderDetailPage Content">
          {contextHolder}
          <BreadCrumb breadCrumbs={[{ text: 'Orders', url: isPartnerViewer ? '/partner/orders' : '/orders' }]} />
          <Empty style={{ marginTop: 96 }} description="This order doesn't exist. It may have been deleted.">
            <Button type="primary" onClick={() => navigate(isPartnerViewer ? '/partner/orders' : '/admin/orders')}>
              Back to orders
            </Button>
          </Empty>
        </div>
      );
    }
    return <Spin style={{ width: '100vw', height: '100vh' }} spinning={isFetching}></Spin>;
  }

  const itemsStatus = [
    {
      title: <div className="status order assigned">Assigned</div>,
      description: statusDatetime('ASSIGNED') && moment(statusDatetime('ASSIGNED')).format(DAYTIME_FORMAT),
    },
  ];

  if (order?.status === 'PICKUP_ARRIVED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>{ORDER_STATUSES_DISPLAY[order?.status]}</div>
      ),
      description: statusDatetime('PICKUP_ARRIVED') && moment(statusDatetime('PICKUP_ARRIVED')).format(DAYTIME_FORMAT),
    });
  } else if (order?.status === 'PICKUP_FAILED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>{ORDER_STATUSES_DISPLAY[order?.status]}</div>
      ),
      description: statusDatetime('PICKUP_ARRIVED') && moment(statusDatetime('PICKUP_ARRIVED')).format(DAYTIME_FORMAT),
    });
  }

  if (order.pickupStatus === 'COLLECTED') {
    itemsStatus.push({
      title: <div className="status order collected">Collected</div>,
      description: statusDatetime('COLLECTED') && moment(statusDatetime('COLLECTED')).format(DAYTIME_FORMAT),
    });

    itemsStatus.push({
      title: <div className="status order in_transit">In Transit</div>,
      description: statusDatetime('IN_TRANSIT') && moment(statusDatetime('IN_TRANSIT')).format(DAYTIME_FORMAT),
    });
  }

  if (order?.status === 'DELIVERY_FAILED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>
          {ORDER_STATUSES_DISPLAY[order?.deliveryStatus]}
        </div>
      ),
      description:
        statusDatetime('DELIVERY_FAILED') && moment(statusDatetime('DELIVERY_FAILED')).format(DAYTIME_FORMAT),
    });
  } else if (order?.status === 'DELIVERY_ARRIVED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>
          {ORDER_STATUSES_DISPLAY[order?.status?.replace('_', ' ')]}
        </div>
      ),
      description:
        statusDatetime('DELIVERY_ARRIVED') && moment(statusDatetime('DELIVERY_ARRIVED')).format(DAYTIME_FORMAT),
    });
  }

  if (order?.status === 'SKIPPED') {
    itemsStatus.push({
      title: <div className="status order skipped">Skipped</div>,
      description: statusDatetime('SKIPPED') && moment(statusDatetime('SKIPPED')).format(DAYTIME_FORMAT),
    });
  }

  itemsStatus.push({
    title: <div className="status order completed">Completed</div>,
    description: statusDatetime('COMPLETED') && moment(statusDatetime('COMPLETED')).format(DAYTIME_FORMAT),
  });

  return (
    <Spin spinning={isFetching && !order?.id}>
      <div className="OrderDetailPage Content">
        {contextHolder}
        <BreadCrumb breadCrumbs={breadCrumbs} />
        {/* OVO-116 — a soft-deleted order still renders here (findOne doesn't */}
        {/* filter); flag it clearly + offer Restore. */}
        {order?.deletedAt && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="This order is in the trash"
            description={`Deleted${order.deletedByUser?.name ? ' by ' + order.deletedByUser.name : ''} on ${moment(
              order.deletedAt,
            ).format(DAYTIME_FORMAT)}. It's hidden from the orders list — restore it to bring it back.`}
            action={
              !isPartnerViewer && (
                <Popconfirm
                  title="Restore order"
                  description={`Restore ${order.orderCode} from the trash?`}
                  onConfirm={handleRestoreOrder}
                  okText="Restore"
                  cancelText="Cancel"
                >
                  <Button size="small" type="primary">
                    Restore
                  </Button>
                </Popconfirm>
              )
            }
          />
        )}
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div className="OrderInfo white border-bottom">
              <div className="OrderInfoHeader">
                <div className="left">
                  <div className="orderId" onClick={() => onClickCopy(order.orderId)}>
                    {order.orderCode}
                    <img src="/icons/file_copy.svg" alt="copy" />
                  </div>
                  {order.status && (
                    <span className={'status order ' + String(order.status).toLowerCase()}>
                      {String(order.status).toLowerCase().replace(/_/g, ' ')}
                    </span>
                  )}
                  {(order.types || []).map((ty) => (
                    <span key={ty} className="order-type-chip">
                      {ty}
                    </span>
                  ))}
                  {order?.deliveryAttempts > 0 && (
                    <Tag
                      color="orange"
                      title="Rescheduled after a failed attempt — see the order timeline for the history"
                    >
                      Attempt #{order.deliveryAttempts + 1} · {order.deliveryAttempts} failed before
                    </Tag>
                  )}
                </div>
                <div className="right">
                  <div className="createdInfo">
                    Created {!!order.createdBy && ' From '}
                    {!!order.createdBy && <b>{order.createdBy}</b>} at{' '}
                    <b>{moment(order.createdAt).format(DAYTIME_FORMAT)}</b>
                  </div>
                  <div className="headerActions">
                    {order?.trackingToken && (
                      <Popover
                        overlayClassName="share-content-order"
                        placement="bottomLeft"
                        content={content}
                        trigger="click"
                      >
                        <Button className="ButtonWhite orderButton" size="small">
                          <ReactSVG className="icon upload" src="/icons/share.svg" />
                          Share tracking
                        </Button>
                      </Popover>
                    )}
                    {/* OVO-116 — soft-delete (move to trash). Pre-route only; */}
                    {/* a deleted order shows the trash banner + Restore instead. */}
                    {!isPartnerViewer && !order?.deletedAt && isDeletable && (
                      <Popconfirm
                        title="Delete order"
                        description={`Move ${order.orderCode} to the trash? You can restore it later.`}
                        onConfirm={handleDeleteOrder}
                        okText="Delete"
                        cancelText="Cancel"
                        okButtonProps={{ danger: true }}
                      >
                        <Button className="orderButton" danger size="small">
                          Delete
                        </Button>
                      </Popconfirm>
                    )}
                    {!isPartnerViewer && (
                      <Button
                        className="ButtonPrimary orderButton"
                        type="primary"
                        size="small"
                        onClick={onClickEditInfo}
                      >
                        Edit
                        <ReactSVG className="icon" src="/icons/edit.svg" />
                      </Button>
                    )}
                    <Button type="text" size="small" className="settingsButton" onClick={fieldConfig.openDrawer}>
                      <ReactSVG className="icon" src="/icons/settings.svg" />
                    </Button>
                  </div>
                </div>
              </div>
              {/* Identity line — the fields every reader wants first (the grid
                  below is the operator's configurable detail set). */}
              <div className="OrderInfoMeta">
                {!tenantConfig.isSingleTenant && order.partnerRef?.name && (
                  <span className="meta-partner">
                    <PartnerLogo name={order.partnerRef.name} logoKey={order.partnerRef.logoFile?.key} size={18} />
                    {isPartnerViewer ? (
                      order.partnerRef.name
                    ) : (
                      <Link to={`/admin/partners/${order.partnerRef.id}`}>{order.partnerRef.name}</Link>
                    )}
                  </span>
                )}
                {order.orderRef && <span>Ref {order.orderRef}</span>}
                {order.partnerOrderRef && <span>Partner ref {order.partnerOrderRef}</span>}
                {order.deliveryDate && <span>{moment(order.deliveryDate).format('ddd, MMM Do YYYY')}</span>}
                {order.routeId && (
                  <span>
                    Route{' '}
                    <Link to={isPartnerViewer ? `/partner/routes/${order.routeId}` : `/admin/routes/${order.routeId}`}>
                      #{order.routeId}
                    </Link>
                    {order.rider?.user?.name ? ` · ${order.rider.user.name}` : ''}
                  </span>
                )}
                {order?.scheduleId && !isPartnerViewer && (
                  <span>
                    <Link
                      to={`/admin/recurring?partnerId=${order.partnerRef?.id ?? order.partnerId ?? ''}`}
                      title={`Created automatically by recurring schedule #${order.scheduleId}`}
                    >
                      Recurring #{order.scheduleId}
                    </Link>
                  </span>
                )}
              </div>
              <div className="OrderInfoContent">
                <Row gutter={[24, 16]}>
                  {fieldConfig.visibleColumns.map((field) => (
                    <Col span={6} key={field.key}>
                      <label>{field.title}</label>
                      {field.render(order)}
                    </Col>
                  ))}
                </Row>
              </div>
              {/* OVO-014 — editable Products block for ADMIN/USER. Replaces */}
              {/* the legacy read-only display below (still kept for partner */}
              {/* viewers who can't edit). */}
              {!isPartnerViewer && (
                <div className="OrderInfoContent OrderProducts">
                  <OrderProductsEditor order={order} onSaved={() => dispatch(doGetOrder({ id }))} />
                </div>
              )}
              {isPartnerViewer && order?.orderProducts?.length > 0 && (
                <div className="OrderInfoContent OrderProducts">
                  <Divider orientation="left" style={{ marginTop: 8 }}>
                    Products
                  </Divider>
                  <Row gutter={[24, 12]}>
                    {order.orderProducts.map((op) => {
                      const ptype = op.partnerItemType || {};
                      const driverItems = (order.collectedItems || []).filter(
                        (ci) => ci.partnerItemTypeId === op.partnerItemTypeId,
                      );
                      const driverItem = driverItems[0];
                      const dispatchWeight = op.weight;
                      const driverWeight = driverItem?.weight ?? null;
                      const effective = dispatchWeight != null ? dispatchWeight : driverWeight;
                      const fromDriver = dispatchWeight == null && driverWeight != null;
                      const stillPending = ptype.requiresWeight && dispatchWeight == null && driverWeight == null;
                      const driverName = driverItem?.collectedByUser?.name || driverItem?.collectedByUser?.email;
                      const driverTagText = `Weight by driver${driverName ? ` · ${driverName}` : ''}`;
                      return (
                        <Col span={12} key={op.id}>
                          <div className="order-product-card">
                            <div className="opc-row">
                              <span className="opc-name">{ptype.name || 'Product'}</span>
                              <span className="opc-qty">
                                {ptype.requiresWeight
                                  ? effective != null
                                    ? `${effective} ${ptype.unitOfMeasure || 'kg'}`
                                    : `— ${ptype.unitOfMeasure || 'kg'}`
                                  : `${op.quantity} ${ptype.unitOfMeasure || 'pcs'}`}
                              </span>
                            </div>
                            <div className="opc-meta">
                              {fromDriver && <Tag color="blue">{driverTagText}</Tag>}
                              {stillPending && <Tag color="orange">Awaiting driver weight</Tag>}
                              {dispatchWeight != null && <Tag color="default">Pre-filled by dispatcher</Tag>}
                              {driverItem?.collectedAt && (
                                <span className="opc-time">
                                  {moment(driverItem.collectedAt).format(DAYTIME_FORMAT)}
                                </span>
                              )}
                            </div>
                          </div>
                        </Col>
                      );
                    })}
                  </Row>
                </div>
              )}
              {/* Empties collected at this order's stops (Diego, Aug 2026) —
                  EMPTY-category collected items only (the PRODUCT captures
                  above are the pickup-weight enrichment). Driver identity is
                  admin-only (OVO-002). */}
              {(order?.collectedItems || []).some((ci) => ci.partnerItemType?.category === 'EMPTY') && (
                <div className="OrderInfoContent OrderProducts">
                  <Divider orientation="left" style={{ marginTop: 8 }}>
                    ♻️ Empties collected
                  </Divider>
                  <Row gutter={[24, 12]}>
                    {(order.collectedItems || [])
                      .filter((ci) => ci.partnerItemType?.category === 'EMPTY')
                      .map((ci) => (
                        <Col span={12} key={ci.id}>
                          <div className="order-product-card">
                            <div className="opc-row">
                              <span className="opc-name">{ci.partnerItemType?.name || 'Empty'}</span>
                              <span className="opc-qty">
                                {ci.quantity || 1} {ci.partnerItemType?.unitOfMeasure || 'pcs'}
                                {ci.weight != null ? ` · ${ci.weight} kg` : ''}
                              </span>
                            </div>
                            <div className="opc-meta">
                              {!isPartnerViewer && (ci.collectedByUser?.name || ci.collectedByUser?.email) && (
                                <Tag color="green">
                                  {'Collected by ' + (ci.collectedByUser?.name || ci.collectedByUser?.email)}
                                </Tag>
                              )}
                              {ci.collectedAt && (
                                <span className="opc-time">{moment(ci.collectedAt).format(DAYTIME_FORMAT)}</span>
                              )}
                            </div>
                          </div>
                        </Col>
                      ))}
                  </Row>
                </div>
              )}
            </div>
            <div className="statuses-log white border-bottom">
              <div className="header">
                {/* OVO-002 — no driver identity on the partner portal. */}
                {!isPartnerViewer && (
                  <div className="Info Rider">
                    <label>Rider: </label>
                    <div className="Text">{order.rider?.user?.name || 'Unassigned'}</div>
                  </div>
                )}
                <div className="button-history">
                  {/* <Button size="small">
                    History Log
                    <ReactSVG className="icon" src="/icons/history.svg" />
                  </Button> */}
                </div>
              </div>
              <div className="statuses">
                <Steps current={getProgressStatus(order.status)} labelPlacement="vertical" items={itemsStatus} />
              </div>
            </div>
            <div className="super-note white">
              <p>
                <b>Support Note:</b> -
              </p>
            </div>
          </Col>
          <Col xs={24} lg={12} className="order-col order-col-pickup">
            <div className="PartnerInfo Pickup white">
              <div className="section-title">
                {isServiceOrder ? (
                  <>
                    <span className="stop-chip service">Service</span>
                    <Tag color="purple" style={{ marginLeft: 8 }}>
                      <ToolOutlined /> single visit
                    </Tag>
                  </>
                ) : (
                  <span className="stop-chip pickup">Pickup</span>
                )}
                {order?.productDetailsKnown === false && (
                  <Tag color="orange" style={{ marginLeft: 8 }}>
                    Pickup enrichment
                  </Tag>
                )}
              </div>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div className="header left">
                    <div className="text">
                      <h5>
                        {order?.pickupName || '-'}
                        {/* OVO-101 — resolved Site badge next to the free-text
                            name. The free-text remains the order's snapshot;
                            the badge surfaces the canonical client identity. */}
                        {renderSiteBadge(order?.pickupSite)}
                        {/* OVO-206 — linked collection box. The findOne payload
                            has carried collectionPoint since OVO-206; this badge
                            is the missing display half (a Site holds 1..N boxes,
                            the box number is the on-the-ground identity). */}
                        {order?.collectionPoint && (
                          <Tag
                            color="purple"
                            style={{
                              marginLeft: 8,
                              fontSize: 11,
                              cursor: isPartnerViewer ? undefined : 'pointer',
                            }}
                            title={
                              order.collectionPoint.capacityKg
                                ? `Capacité ${order.collectionPoint.capacityKg} kg`
                                : undefined
                            }
                            onClick={
                              isPartnerViewer
                                ? undefined
                                : () => navigate(`/admin/collection-points/${order.collectionPoint.id}`)
                            }
                          >
                            {/^(box|caisse)/i.test(order.collectionPoint.name)
                              ? order.collectionPoint.name
                              : `Box: ${order.collectionPoint.name}`}
                          </Tag>
                        )}
                      </h5>
                      <span>{order?.pickupEmail || '-'}</span>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="actions header right">
                    {!isPartnerViewer && (
                      <Button type="link" size="small" onClick={onClickEditPickup}>
                        Edit
                        <ReactSVG className="icon" src="/icons/edit.svg" />
                      </Button>
                    )}
                    {order.routePlanId && (
                      <Button
                        type="link"
                        size="small"
                        disabled={!order.routePlanId}
                        onClick={() => goRoutePlanDetail(order, isServiceOrder ? STEP_TYPE.service : STEP_TYPE.pickup)}
                      >
                        View on Route Plan
                        <ReactSVG className="icon" src="/icons/routePlan.svg" />
                      </Button>
                    )}
                  </div>
                </Col>
                <Col span={24}>
                  <label>Address</label>
                  <div className="order-content hasCopy" onClick={() => onClickCopy(order.pickupAddress)}>
                    {order.pickupAddress}
                    <img src="/icons/file_copy.svg" />
                  </div>
                </Col>
                <Col span={8}>
                  <label>Phone Number</label>
                  <div className="order-content">{order.pickupPhone}</div>
                </Col>
                <Col span={8}>
                  <label>{isServiceOrder ? 'Service Time Window' : 'Pickup Time Window'}</label>
                  <div className="order-content">
                    {(isServiceOrder ? order.serviceTimeStart || order.pickupTimeStart : order.pickupTimeStart) || ''} -{' '}
                    {(isServiceOrder ? order.serviceTimeEnd || order.pickupTimeEnd : order.pickupTimeEnd) || ''}
                  </div>
                </Col>
                <Col span={8}>
                  <div>
                    <label>Duration</label>
                    <div className="order-content">
                      {(() => {
                        const d = isServiceOrder ? order.serviceDuration || order.pickupDuration : order.pickupDuration;
                        return d ? d + (d > 1 ? ' minutes' : ' minute') : '';
                      })()}
                    </div>
                  </div>
                </Col>
                <Col span={24}>
                  <label>Note</label>
                  <div className="order-content">{order.pickupNote || '-'}</div>
                </Col>
              </Row>
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <label>{isServiceOrder ? 'Photos' : 'POP'}</label>
                  <div className="order-content images">
                    <Image.PreviewGroup>
                      {filePickups.map((file) => (
                        <Image key={file.id} width={100} src={user.host + '/' + file.file.key} />
                      ))}
                    </Image.PreviewGroup>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          {order.orderType !== ORDER_TYPES.SERVICE && (
            <Col xs={24} lg={12} className="order-col order-col-delivery">
              <div className="PartnerInfo Delivery white">
                <div className="section-title">
                  <span className="stop-chip delivery">Delivery</span>
                </div>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="header left">
                      <div className="text">
                        <h5>
                          {order?.deliveryName || '-'}
                          {/* OVO-101 — resolved Site badge (delivery side). */}
                          {renderSiteBadge(order?.deliverySite)}
                        </h5>
                        <span>{order?.deliveryEmail || '-'}</span>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="actions header right">
                      {!isPartnerViewer && !order.routePlanId && (
                        <Button type="link" size="small" onClick={onClickEditDelivery}>
                          Edit
                          <ReactSVG className="icon" src="/icons/edit.svg" />
                        </Button>
                      )}
                      {order.routePlanId && (
                        <Button
                          type="link"
                          size="small"
                          disabled={!order.routePlanId}
                          onClick={() => goRoutePlanDetail(order, STEP_TYPE.delivery)}
                        >
                          View on Route Plan
                          <ReactSVG className="icon" src="/icons/routePlan.svg" />
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col span={24}>
                    <label>Address</label>
                    <div className="order-content hasCopy" onClick={() => onClickCopy(order.deliveryAddress)}>
                      {order.deliveryAddress}
                      <img src="/icons/file_copy.svg" />
                    </div>
                  </Col>
                  <Col span={8}>
                    <label>Phone Number</label>
                    <div className="order-content">{order.deliveryPhone}</div>
                  </Col>
                  <Col span={8}>
                    <label>Delivery Time Window</label>
                    <div className="order-content">
                      {order.deliveryTimeStart || ''} - {order.deliveryTimeEnd || ''}
                    </div>
                  </Col>
                  <Col span={8}>
                    <div>
                      <label>Duration</label>
                      <div className="order-content">
                        {order.deliveryDuration
                          ? order.deliveryDuration + (order.deliveryDuration > 1 ? ' minutes' : ' minute')
                          : '-'}
                      </div>
                    </div>
                  </Col>
                  <Col span={24}>
                    <label>Note</label>
                    <div className="order-content">{order.deliveryNote || '-'}</div>
                  </Col>
                </Row>
                <Row gutter={[24, 16]}>
                  <Col span={24}>
                    <label>POD</label>
                    <div className="order-content images">
                      <Image.PreviewGroup>
                        {fileDeliveries.map((file) => (
                          <Image key={file.id} width={100} src={user.host + '/' + file.file.key} />
                        ))}
                      </Image.PreviewGroup>
                    </div>
                  </Col>
                </Row>
                {order?.trackingToken && (
                  <Row gutter={[24, 16]} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <label>Tracking URL</label>
                      <div className="order-content">
                        <a
                          href={`${window.location.origin}/t/${order.orderCode}/${order.trackingToken}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${window.location.origin}/t/${order.orderCode}/${order.trackingToken}`}
                        </a>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </Col>
          )}
          <Col span={24} className="order-col order-col-map">
            <div className="wrapper-map white">
              <GoogleMap markers={markers} polylines={polylines} className="mapbox-order-detail" />
            </div>
          </Col>
          <Col span={24}>
            <div className="DeliveryDetail white">
              <div className="section-title">
                <span className="stop-chip driver">Driver report</span>
              </div>
              <Row>
                {/* <Col span={24}>
                  <div className="wrapper border-bottom">
                    <div className="title">Delivery Status</div>
                  </div>
                </Col> */}
                <Col span={24}>
                  <div className="wrapper">
                    <div className="title">Delivery Note</div>
                    <div className="content">{order.packageWas || '-'}</div>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="wrapper">
                    <div className="title">Pickup Note</div>
                    <div className="content">{order.pickupMobileNote || '-'}</div>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="wrapper border-top">
                    <div className="title">Rider Note</div>
                    <div className="content">{order.customerNote || '-'}</div>
                  </div>
                </Col>
                {order.issueType && (
                  <Col span={24}>
                    <div className="wrapper border-top driver-report-section">
                      <div className="title">Driver Report</div>
                      <div className="content">
                        <div className="driver-report-issue">
                          <span className="driver-report-label">Issue:</span>{' '}
                          {ISSUE_TYPES[order.issueType] || order.issueType}
                        </div>
                        {order.customerNote && (
                          <div className="driver-report-note">
                            <span className="driver-report-label">Note:</span> {order.customerNote}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
                {order.scannedCode && (
                  <Col span={24}>
                    <div className="wrapper border-top scanned-code-section">
                      <div className="title">Scanned Code</div>
                      <div className="content">
                        <span className="scanned-code-value">{order.scannedCode}</span>
                        {order.scannedCodeType && <span className="scanned-code-type">({order.scannedCodeType})</span>}
                        {order.scannedAt && (
                          <div className="scanned-code-time">
                            Scanned at: {moment(order.scannedAt).format(DAYTIME_FORMAT)}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
                {order.collectedItems?.length > 0 && (
                  <Col span={24}>
                    <div className="wrapper border-top collected-items-section">
                      <div className="title">Collected Items ({order.collectedItems.length})</div>
                      <div className="content">
                        <div className="collected-items-list">
                          {order.collectedItems.map((item) => (
                            <div key={item.id} className="collected-item-row">
                              <span className="collected-item-name">
                                {item.partnerItemType?.name || item.description || 'Item'}
                              </span>
                              {item.collectionPoint && (
                                <Tag
                                  color="geekblue"
                                  style={{
                                    marginInlineEnd: 0,
                                    cursor: isPartnerViewer ? undefined : 'pointer',
                                  }}
                                  onClick={
                                    isPartnerViewer
                                      ? undefined
                                      : () => navigate(`/admin/collection-points/${item.collectionPoint.id}`)
                                  }
                                >
                                  {item.collectionPoint.externalRef
                                    ? `Box ${item.collectionPoint.externalRef}`
                                    : item.collectionPoint.name}
                                </Tag>
                              )}
                              {item.fillTenths != null && (
                                <span className="collected-item-fill">fill {item.fillTenths}/10</span>
                              )}
                              {item.scannedCode && <span className="collected-item-code">{item.scannedCode}</span>}
                              {item.weight != null && (
                                <span className="collected-item-weight">
                                  {item.weight} {item.partnerItemType?.unitOfMeasure || 'kg'}
                                </span>
                              )}
                              {item.quantity > 1 && <span className="collected-item-qty">x{item.quantity}</span>}
                              {item.partnerItemType?.category && (
                                <span
                                  className={`collected-item-category ${item.partnerItemType.category.toLowerCase()}`}
                                >
                                  {item.partnerItemType.category}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Col>
                )}
                {(order.partnerId || order.partner) && (
                  <Col span={24}>
                    <div className="wrapper border-top webhook-logs-section">
                      <div className="title clickable" onClick={toggleWebhookLogs}>
                        Webhook Logs {webhookLogsLoaded && `(${webhookLogs.length})`}
                        <span className={`expand-icon ${webhookLogsOpen ? 'open' : ''}`}>&#9660;</span>
                      </div>
                      {webhookLogsOpen && (
                        <div className="content">
                          {webhookLogs.length === 0 && webhookLogsLoaded && (
                            <div className="no-logs">No webhook calls recorded</div>
                          )}
                          {webhookLogs.length > 0 && (
                            <Table
                              dataSource={webhookLogs}
                              rowKey="id"
                              size="small"
                              pagination={false}
                              className="webhook-logs-table"
                              expandable={{
                                expandedRowRender: (log) => (
                                  <div className="webhook-log-detail">
                                    <div className="webhook-detail-section">
                                      <div className="webhook-detail-label">Request Body</div>
                                      <pre className="webhook-body">{JSON.stringify(log.requestBody, null, 2)}</pre>
                                    </div>
                                    <div className="webhook-detail-section">
                                      <div className="webhook-detail-label">Response</div>
                                      <pre className="webhook-body">
                                        {log.errorMessage
                                          ? `Error: ${log.errorMessage}`
                                          : typeof log.responseBody === 'string' && log.responseBody.startsWith('{')
                                            ? JSON.stringify(JSON.parse(log.responseBody), null, 2)
                                            : log.responseBody || '(empty)'}
                                      </pre>
                                    </div>
                                  </div>
                                ),
                              }}
                              columns={[
                                {
                                  title: 'Status',
                                  dataIndex: 'responseStatus',
                                  key: 'status',
                                  width: 70,
                                  render: (status) => (
                                    <Tag color={status >= 200 && status < 300 ? 'green' : status ? 'red' : 'orange'}>
                                      {status || 'Err'}
                                    </Tag>
                                  ),
                                },
                                {
                                  title: 'Event',
                                  dataIndex: 'eventType',
                                  key: 'event',
                                  width: 140,
                                  render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
                                },
                                {
                                  title: 'Time',
                                  dataIndex: 'createdAt',
                                  key: 'time',
                                  width: 160,
                                  render: (val) => moment(val).format('DD/MM/YYYY HH:mm:ss'),
                                },
                                {
                                  title: 'Duration',
                                  dataIndex: 'durationMs',
                                  key: 'duration',
                                  width: 80,
                                  render: (val) => (val != null ? `${val}ms` : '-'),
                                },
                                ...(!isPartnerViewer
                                  ? [
                                      {
                                        title: '',
                                        key: 'action',
                                        width: 70,
                                        render: (_, log) => (
                                          <Button
                                            type="link"
                                            size="small"
                                            loading={retryingLogId === log.id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRetryWebhook(log.id);
                                            }}
                                          >
                                            Retry
                                          </Button>
                                        ),
                                      },
                                    ]
                                  : []),
                              ]}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
          <Col span={24} className="order-col order-col-operator">
            {/* OVO-020 — operator-simulated driver actions. Admin/USER only; */}
            {/* partner viewers get nothing here. Hidden when the order isn't */}
            {/* on a route (no route → no step ids to act on). */}
            {!isPartnerViewer && order?.routeId && (
              <div style={{ marginBottom: 12 }}>
                <Button block loading={driverViewLoading} onClick={handleDriverView}>
                  👁 Driver view (dry-run)
                </Button>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4, textAlign: 'center' }}>
                  Opens the driver app at this stop — a preview, nothing is saved.
                </div>
              </div>
            )}
            {!isPartnerViewer && order?.routeId && (
              <OperatorActionPanel order={order} onSuccess={() => dispatch(doGetOrder({ id }))} />
            )}
            {/* Fix recorded pickup/delivery times (driver logged a stop late).
                Self-hides when the order has no editable status log yet, so it
                also covers a collected-but-not-yet-delivered order. */}
            {!isPartnerViewer && order?.routeId && (
              <OrderActualTimeEditor order={order} onSuccess={() => dispatch(doGetOrder({ id }))} />
            )}
            {/* OVO-117 — the order's support tickets + create-with-context. */}
            {!isPartnerViewer && <OrderTicketsPanel order={order} />}
          </Col>
        </Row>
        {/* OVO-103 — per-step photos the dispatcher uploads to help the driver. */}
        {!isPartnerViewer && order?.orderCode && (
          <OrderMediaSection orderCode={order.orderCode} viewOnly={isPartnerViewer} />
        )}
        {/* OVO-014 + OVO-020 — unified order timeline (driver actions + */}
        {/* operator-simulated transitions + operator audit edits). */}
        {!isPartnerViewer && order && (
          <OrderTimeline order={order} canUndo onUndone={() => dispatch(doGetOrder({ id }))} />
        )}
        {!isPartnerViewer && (
          <Collapse
            ghost
            size="small"
            style={{ marginTop: 16 }}
            items={[
              {
                key: 'advanced',
                label: <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>Advanced</span>,
                children: (
                  <pre
                    style={{
                      background: 'var(--surface-2)',
                      padding: 16,
                      borderRadius: 4,
                      fontSize: 11,
                      maxHeight: 500,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {JSON.stringify(order, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
        )}
      </div>
      <Modal
        width={1128}
        closeIcon={false}
        title={titleModal || 'Edit Order Info'}
        open={isModalOpen}
        forceRender
        footer={false}
        onCancel={cancelModal}
      >
        <Spin spinning={isFetching}>
          <div className="OrderDetailModal">
            <Form
              form={form}
              scrollToFirstError
              layout="vertical"
              requiredMark={false}
              colon={false}
              className="FormContent"
              onFinish={onFinishForm}
              onValuesChange={onValuesChange}
            >
              {actionPage === typePage.order.type && (
                <OrderInfoForm order={order} types={types} setTypes={setTypes} partners={partners} />
              )}
              {actionPage === typePage.pickup.type && <PickupForm form={form} order={order} />}
              {actionPage === typePage.delivery.type && <DeliveryForm form={form} order={order} />}
              <div className="FormFooter">
                <Button className="ButtonCancel" onClick={cancelModal}>
                  Cancel
                </Button>
                <Button className="CreateButton ButtonPrimary" type="primary" htmlType="submit">
                  Update
                  <ReactSVG className="icon" src="/icons/done.svg" />
                </Button>
              </div>
            </Form>
          </div>
        </Spin>
      </Modal>
      <ColumnConfigDrawer
        open={fieldConfig.drawerOpen}
        onClose={fieldConfig.closeDrawer}
        columns={fieldConfig.orderedAllColumns}
        hidden={fieldConfig.hidden}
        fixedKeys={[]}
        onToggle={fieldConfig.toggleColumn}
        onMove={fieldConfig.moveColumn}
        onReset={fieldConfig.resetConfig}
        hidePageSize
      />
    </Spin>
  );
};

export default OrderDetailPage;
